import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "app/components/ui/dialog";
import { Button } from "app/components/ui/button";

// YouTube API types
declare global {
  interface Window {
    YT: {
      Player: any;
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface VideoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  aspectRatio?: string;
}

export const VideoDialog: React.FC<VideoDialogProps> = ({
  isOpen,
  onOpenChange,
  videoId,
  aspectRatio = "aspect-video",
}) => {
  const playerRef = useRef<any>(null);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  // Calculate width classes based on aspect ratio
  const getWidthClasses = () => {
    // Default 16:9 aspect ratio
    let widthClass = "w-[calc(80vh*16/9)]";
    let maxWidthClass =
      "max-w-[90vw] sm:max-w-[85vw] md:max-w-[80vw] lg:max-w-[70vw] xl:max-w-[60vw]";

    // Check if aspect ratio contains 9/16 (vertical video)
    if (aspectRatio?.includes("aspect-[9/16]")) {
      widthClass = "w-[calc(80vh*9/16)]";

      // For mobile-first vertical that becomes horizontal on md
      if (aspectRatio.includes("md:aspect-[16/9]")) {
        widthClass = "w-[calc(80vh*9/16)] md:w-[calc(80vh*16/9)]";
        maxWidthClass =
          "max-w-[20rem] md:max-w-[80vw] lg:max-w-[70vw] xl:max-w-[60vw]";
      }
    }
    // For explicit 16:9
    else if (
      aspectRatio === "aspect-video" ||
      aspectRatio === "aspect-[16/9]"
    ) {
      widthClass = "w-[calc(80vh*16/9)]";
    }
    // For custom aspect ratio like 4:3
    else if (aspectRatio?.includes("aspect-[4/3]")) {
      widthClass = "w-[calc(80vh*4/3)]";
    }
    // For custom aspect ratio like 1:1
    else if (
      aspectRatio?.includes("aspect-[1/1]") ||
      aspectRatio === "aspect-square"
    ) {
      widthClass = "w-[calc(80vh*1)]";
    }

    return { widthClass, maxWidthClass };
  };

  const { widthClass, maxWidthClass } = getWidthClasses();

  // Load YouTube API
  useEffect(() => {
    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        setIsApiLoaded(true);
        return;
      }

      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        setIsApiLoaded(true);
      };
    };

    loadYouTubeAPI();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  const initializePlayer = () => {
    if (!videoRef.current || !isApiLoaded) return;

    // Clear any existing content
    videoRef.current.innerHTML = "";

    playerRef.current = new window.YT.Player(videoRef.current, {
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        controls: 1,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        mute: 0,
        fs: 1,
        iv_load_policy: 3,
        disablekb: 0,
        playsinline: 1,
        cc_load_policy: 0,
        enablejsapi: 1,
      },
      events: {
        onReady: (event: any) => {
          playerRef.current = event.target;
        },
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      // Initialize player when dialog opens
      setTimeout(() => {
        initializePlayer();
      }, 100);
    } else {
      // Clean up when dialog closes
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isApiLoaded, videoId]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        // overlayClassName="bg-black/75 backdrop-blur-[0px]"
        className={`top-1/2 ${maxWidthClass} ${widthClass} ${aspectRatio} max-h-[80vh] p-0 bg-black border-none sm:rounded-xl lg:rounded-2xl shadow-none overflow-visible [&>button]:hidden text-left`}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        aria-label="Video Player"
        aria-describedby="Video Player"
      >
        <DialogTitle className="sr-only">Video Player</DialogTitle>
        <div className="relative">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute -top-2.5 sm:-top-3 lg:-top-3.5 -right-2.5 sm:-right-3 lg:-right-3.5 z-50 size-6 sm:size-7 lg:size-8 rounded-full bg-black/50 hover:bg-black/70 hover:text-white"
            onClick={() => onOpenChange(false)}
            aria-label="Close video"
          >
            <img
              src="/assets/close.svg"
              alt="Close"
              className="filter invert size-4 sm:size-5 lg:size-6"
            />
          </Button>
          {/* YouTube Player Container */}
          <div
            ref={videoRef}
            className="size-full rounded-xl lg:rounded-2xl overflow-hidden"
          />
        </div>
      </DialogContent>
      {/* Custom CSS to show only basic controls */}
      <style>{`
        .ytp-chrome-top,
        .ytp-watermark,
        .ytp-show-cards-title,
        .ytp-title,
        .ytp-chapter-title,
        .ytp-menuitem,
        .ytp-button:not(.ytp-play-button):not(.ytp-next-button):not(.ytp-previous-button),
        .ytp-volume-panel,
        .ytp-settings-button,
        .ytp-fullscreen-button,
        .ytp-pause-overlay,
        .ytp-gradient-top,
        .ytp-gradient-bottom:not(.ytp-progress-bar-container) {
            display: none !important;
            visibility: hidden !important;
        }
        
        .ytp-progress-bar-container {
            opacity: 1 !important;
            visibility: visible !important;
        }
        
        .ytp-play-button,
        .ytp-next-button,
        .ytp-previous-button {
            opacity: 1 !important;
            visibility: visible !important;
        }
        `}</style>
    </Dialog>
  );
};
